import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { parseCommittedMoney } from '../../utils/snoop/format'

/** Synthetic column the DataTable injects to host the pencil control. */
export const PUB_MAPPING_EDIT_COLUMN = '__pub_mapping_edit__'

function displayValue(row, column) {
  const raw = row?.[column]
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s || ['nan', 'none', 'null', '—'].includes(s.toLowerCase())) return ''
  return s
}

/** Money cells arrive formatted ($78.75k); edit the underlying whole dollars. */
function moneyInputFromRow(row, column) {
  const amount = parseCommittedMoney(row?.[column])
  return amount == null ? '' : String(amount)
}

const inputClass =
  'w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none'

const labelClass = 'block text-xs font-medium text-gray-600'

/**
 * Pencil control → edit the pub_mappings fields that are not dropdowns
 * (Name, Genius artist id, Lawyer, Calc, the money fields, Notes).
 *
 * Status / Lead A&R / Action stay inline on the row; this covers everything
 * else, which otherwise could only be set at add time.
 */
export default function PubMappingEdit({ row, saving = false, error = null, onSave }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [geniusId, setGeniusId] = useState('')
  const [lawyer, setLawyer] = useState('')
  const [calc, setCalc] = useState('')
  const [committedMoney, setCommittedMoney] = useState('')
  const [uncommitted, setUncommitted] = useState('')
  const [optionMoney, setOptionMoney] = useState('')
  const [notes, setNotes] = useState('')
  const [localError, setLocalError] = useState(null)
  const nameRef = useRef(null)

  function closeMenu() {
    setOpen(false)
    setLocalError(null)
  }

  function openMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    setName(displayValue(row, 'Name'))
    setGeniusId(displayValue(row, 'Genius Artist ID'))
    setLawyer(displayValue(row, 'Lawyer'))
    setCalc(displayValue(row, 'Calc'))
    setCommittedMoney(moneyInputFromRow(row, 'Committed $'))
    setUncommitted(moneyInputFromRow(row, 'Uncommitted'))
    setOptionMoney(displayValue(row, 'Option'))
    setNotes(displayValue(row, 'Notes'))
    setLocalError(null)
    setOpen(true)
  }

  useEffect(() => {
    if (open && nameRef.current) {
      nameRef.current.focus()
      nameRef.current.select()
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    function onKey(e) {
      if (e.key === 'Escape') closeMenu()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSave(e) {
    e.preventDefault()
    e.stopPropagation()
    setLocalError(null)
    const nextName = name.trim()
    const nextGenius = geniusId.trim()
    if (!nextName && !nextGenius) {
      setLocalError('Name or Genius artist id is required')
      return
    }
    if (!onSave) return
    // Close immediately; the row shows the in-flight saving state.
    closeMenu()
    await onSave({
      name: nextName || null,
      geniusArtistId: nextGenius || null,
      clearGeniusArtistId: !nextGenius,
      lawyer: lawyer.trim() || null,
      calc: calc.trim() || null,
      committedMoney: parseCommittedMoney(committedMoney),
      uncommitted: parseCommittedMoney(uncommitted),
      optionMoney: optionMoney.trim() || null,
      notes: notes.trim() || null,
    })
  }

  const rowLabel = displayValue(row, 'Name') || displayValue(row, 'Genius Artist ID') || 'row'

  return (
    <>
      <button
        type="button"
        onClick={openMenu}
        disabled={saving}
        title={error || `Edit ${rowLabel}`}
        aria-label={`Edit ${rowLabel}`}
        className={[
          'inline-flex h-7 w-7 items-center justify-center rounded-md transition',
          error
            ? 'text-rose-600 hover:bg-rose-50'
            : 'text-gray-500 hover:bg-blue-50 hover:text-[var(--encore-blue)]',
          saving ? 'animate-pulse opacity-70' : '',
        ].join(' ')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open
        ? createPortal(
            <div
              className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16"
              role="dialog"
              aria-modal="true"
              aria-label={`Edit ${rowLabel}`}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeMenu()
              }}
            >
              <form
                onSubmit={handleSave}
                className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
              >
                <h2 className="text-lg font-semibold tracking-tight text-gray-900">
                  Edit mapping
                </h2>
                <p className="mt-0.5 truncate text-xs text-gray-500" title={rowLabel}>
                  {rowLabel}
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="col-span-2">
                    <span className={labelClass}>Name</span>
                    <input
                      ref={nameRef}
                      className={inputClass}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <label className="col-span-2">
                    <span className={labelClass}>Genius Artist ID</span>
                    <input
                      className={inputClass}
                      value={geniusId}
                      onChange={(e) => setGeniusId(e.target.value)}
                      placeholder="blank clears the Genius link"
                      inputMode="numeric"
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Lawyer</span>
                    <input
                      className={inputClass}
                      value={lawyer}
                      onChange={(e) => setLawyer(e.target.value)}
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Calc</span>
                    <input
                      className={inputClass}
                      value={calc}
                      onChange={(e) => setCalc(e.target.value)}
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Committed $</span>
                    <input
                      className={inputClass}
                      value={committedMoney}
                      onChange={(e) => setCommittedMoney(e.target.value)}
                      placeholder="whole dollars"
                      inputMode="numeric"
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Uncommitted</span>
                    <input
                      className={inputClass}
                      value={uncommitted}
                      onChange={(e) => setUncommitted(e.target.value)}
                      placeholder="whole dollars"
                      inputMode="numeric"
                    />
                  </label>
                  <label className="col-span-2">
                    <span className={labelClass}>Option</span>
                    <input
                      className={inputClass}
                      value={optionMoney}
                      onChange={(e) => setOptionMoney(e.target.value)}
                      placeholder="free text, e.g. 2 x 100k"
                    />
                  </label>
                  <label className="col-span-2">
                    <span className={labelClass}>Notes</span>
                    <textarea
                      className={`${inputClass} min-h-[5rem]`}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </label>
                </div>

                {localError ? (
                  <p className="mt-3 text-sm text-red-600">{localError}</p>
                ) : null}

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    onClick={closeMenu}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
